import { StudioArt, StudioPin, StudioRoomState, StudioSceneState, WallSide } from '../types'
import { clampU } from '../babylon/sceneMath'

/** Strip runtime Classic placeholders before history / persist. */
export function persistableArts(arts: StudioArt[]): StudioArt[] {
  return arts.filter((a) => !a.isPlaceholder)
}

const WALL_CYCLE: WallSide[] = [-1, 0, 1, -1, 0, 1]
const EYE_LINE = 2.3

/** Max |u| so a print of `artWidth` stays fully on the wall (not corner-clipped). */
function maxSlideForWidth(side: WallSide, room: { width: number; depth: number }, artWidth: number): number {
  return clampU(side, Number.POSITIVE_INFINITY, room, artWidth)
}

/**
 * Evenly space prints along each wall’s slide axis. Optional shuffle reorders
 * within the wall (Muse). Unlike shuffling existing `u` values, this always
 * yields distinct targets — stacked `u: 0` piles (Classic→Studio leftovers)
 * become a real walkable tour.
 */
export function redistributeArtsByWall(
  arts: StudioArt[],
  room: { width: number; depth: number },
  opts?: { shuffle?: boolean; resetEyeLine?: boolean },
): StudioArt[] {
  const bySide: Record<WallSide, StudioArt[]> = { [-1]: [], 0: [], 1: [] }
  arts.forEach((a) => {
    if (a.isPlaceholder) return
    bySide[a.side].push(a)
  })
  const nextById = new Map<string, StudioArt>()
  ;([-1, 0, 1] as WallSide[]).forEach((side) => {
    let group = [...bySide[side]]
    if (opts?.shuffle) group = group.sort(() => Math.random() - 0.5)
    const maxW = Math.max(1.2, ...group.map((a) => a.w || 1.8))
    const usable = maxSlideForWidth(side, room, maxW) * 2
    // Match autoLayout spirit (≈6–8m clusters) — don't stretch tip-to-tip on
    // Pavilion-scale walls or prints read as "stuck in the corner".
    const aesthetic = (side === 0 ? 6 : 8) + Math.max(0, group.length - 2) * 2
    const spread = Math.min(usable, aesthetic)
    group.forEach((a, i) => {
      const step = group.length > 1 ? spread / (group.length - 1) : 0
      const rawU = group.length > 1 ? -spread / 2 + i * step : 0
      nextById.set(a.id, {
        ...a,
        u: clampU(side, rawU, room, a.w || maxW),
        y: opts?.resetEyeLine || opts?.shuffle ? EYE_LINE + (opts?.shuffle ? (Math.random() - 0.5) * 0.12 : 0) : a.y,
      })
    })
  })
  return arts.map((a) => nextById.get(a.id) || a)
}

/**
 * Re-assign walls in cycle order then space — for Classic→Studio where every
 * slot was stubbed at `side: 0, u: 0`.
 */
export function relayoutArtsAcrossRoom(
  arts: StudioArt[],
  room: { width: number; depth: number },
): StudioArt[] {
  const real = arts.filter((a) => !a.isPlaceholder)
  const placeholders = arts.filter((a) => a.isPlaceholder)
  const reassigned = real.map((a, i) => ({ ...a, side: WALL_CYCLE[i % WALL_CYCLE.length] }))
  return [...redistributeArtsByWall(reassigned, room, { resetEyeLine: true }), ...placeholders]
}

/** True when 2+ non-placeholder prints share nearly the same wall slot. */
export function artsHaveStackedPlacement(arts: StudioArt[]): boolean {
  const keys = new Map<string, number>()
  for (const a of arts) {
    if (a.isPlaceholder) continue
    const key = `${a.side}:${a.u.toFixed(2)}`
    const n = (keys.get(key) || 0) + 1
    if (n >= 2) return true
    keys.set(key, n)
  }
  return false
}

/** True when any print’s frame would clip a corner at the current room size. */
export function artsClipWallCorners(
  arts: StudioArt[],
  room: { width: number; depth: number },
): boolean {
  return arts.some((a) => {
    if (a.isPlaceholder) return false
    const clamped = clampU(a.side, a.u, room, a.w || 1.8)
    return Math.abs(clamped - a.u) > 0.02
  })
}

/**
 * True when prints sit so far toward a wall tip they look wedged in the corner
 * (even if not mathematically clipping). Catches the prior tip-to-tip relayout.
 */
export function artsCrowdedIntoCorners(
  arts: StudioArt[],
  room: { width: number; depth: number },
): boolean {
  return arts.some((a) => {
    if (a.isPlaceholder) return false
    const wallHalf = a.side === 0 ? room.width / 2 : room.depth / 2
    return Math.abs(a.u) + (a.w || 1.8) / 2 > wallHalf - 0.85
  })
}

export function buildSceneState(
  room: StudioRoomState,
  arts: StudioArt[],
  pins: StudioPin[],
): StudioSceneState {
  return { version: 1, room, arts: persistableArts(arts), pins }
}

export function serializeSceneState(state: StudioSceneState): string {
  return JSON.stringify(state)
}

export function sceneStatesEqual(a: StudioSceneState, b: StudioSceneState): boolean {
  return serializeSceneState(a) === serializeSceneState(b)
}

/**
 * Clamp freeform print placement into the current room footprint after a
 * preset swap (Corridor ↔ Loft ↔ Pavilion) so prints don't sit outside walls.
 */
export function reflowArtsForRoom(
  arts: StudioArt[],
  room: { width: number; height: number; depth: number },
): StudioArt[] {
  const yMin = 1.2
  const yMax = Math.max(yMin + 0.2, room.height - 0.6)
  let changed = false
  const next = arts.map((art) => {
    if (art.isPlaceholder) return art
    const u = clampU(art.side, art.u, room, art.w || 1.8)
    const y = Math.max(yMin, Math.min(yMax, art.y))
    if (u === art.u && y === art.y) return art
    changed = true
    return { ...art, u, y }
  })
  return changed ? next : arts
}

/** Resolve Classic slot i → art by stable `classicSlotIndex`, else array order. */
export function artForClassicSlot(arts: StudioArt[], slotIndex: number): StudioArt | undefined {
  const bySlot = arts.find((a) => a.classicSlotIndex === slotIndex)
  if (bySlot) return bySlot
  return arts[slotIndex]
}
